import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface EventRace {
  id: string;
  name: string;
  distance_km: number;
  elevation_gain: number | null;
  terrain: 'road' | 'trail' | 'mixed' | 'track';
  difficulty: 'easy' | 'moderate' | 'challenging' | 'extreme';
  max_participants: number | null;
  current_participants: number | null;
  current_teams?: number | null;
  price_cents: number;
  price_cents_ffa: number | null;
  fees_included: boolean | null;
  start_time: string | null;
  color: string | null;
  sort_order: number | null;
}

export interface Event {
  id: string;
  slug: string;
  organization_id: string;
  title: string;
  description: string | null;
  distance_km: number | null;
  elevation_gain: number | null;
  terrain: 'road' | 'trail' | 'mixed' | 'track';
  difficulty: 'easy' | 'moderate' | 'challenging' | 'extreme';
  event_date: string;
  registration_deadline: string | null;
  location: string;
  city: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  max_participants: number | null;
  current_participants: number;
  price_cents: number;
  commission_rate?: number | null;
  merch_commission_rate?: number | null;
  hero_featured?: boolean;
  currency: string;
  status: 'draft' | 'published' | 'cancelled' | 'completed';
  featured: boolean;
  image_url: string | null;
  created_at: string;
  updated_at: string;
  isDemo?: boolean;
  races?: EventRace[];
  event_category?: 'standard' | 'community' | null;
  requires_pps?: boolean | null;
  registrations_manually_closed?: boolean;
  registrations_closed_at?: string | null;
  registrations_closed_reason?: string | null;
}

// No more mock events - organizations start with empty event list

// Hook to fetch all published events (for runners)
export function usePublishedEvents() {
  return useQuery({
    queryKey: ['published-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select(`
          id, slug, title, distance_km, elevation_gain, terrain, difficulty,
          event_date, registration_deadline, location, city, country, latitude, longitude,
          max_participants, current_participants, price_cents, currency, status, featured,
          image_url, created_at, updated_at, event_category, commission_rate, requires_pps,
        hero_featured,
          event_races (
            id, name, distance_km, elevation_gain, terrain, difficulty,
            max_participants, current_participants, current_teams, price_cents, fees_included,
            start_time, color, sort_order, team_mode, team_size
          )
        `)
        .eq('status', 'published')
        .order('event_date', { ascending: true });

      if (error) throw error;
      
      return data.map((event: any) => ({
        ...event,
        races: event.event_races?.sort((a: EventRace, b: EventRace) => 
          (a.sort_order || 0) - (b.sort_order || 0)
        ) || [],
      })) as Event[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useEvents(organizationId?: string) {
  const queryClient = useQueryClient();

  const { data: events, isLoading, error } = useQuery({
    queryKey: ['events', organizationId],
    queryFn: async () => {
      // Return empty array if no organization
      if (!organizationId) {
        return [];
      }

      const { data, error } = await supabase
        .from('events')
        .select(`
          id, slug, title, description, distance_km, elevation_gain, terrain, difficulty,
          event_date, registration_deadline, location, city, country, latitude, longitude,
          max_participants, current_participants, price_cents, currency, status, featured,
          image_url, created_at, updated_at, organization_id, event_category, commission_rate,
        hero_featured, registrations_manually_closed, registrations_closed_at, registrations_closed_reason,
          event_races (
            id, name, distance_km, elevation_gain, terrain, difficulty,
            max_participants, current_participants, current_teams, price_cents, fees_included,
            start_time, color, sort_order, team_mode, team_size
          )
        `)
        .eq('organization_id', organizationId)
        .order('event_date', { ascending: true });

      if (error) throw error;
      
      // Map event_races to races for consistency
      const eventsWithRaces = data.map((event: any) => ({
        ...event,
        races: event.event_races?.sort((a: EventRace, b: EventRace) => 
          (a.sort_order || 0) - (b.sort_order || 0)
        ) || [],
      }));
      
      return eventsWithRaces as Event[];
    },
    enabled: true, // Always enabled to show mock data
  });

  const createEvent = useMutation({
    mutationFn: async (eventData: Partial<Event>) => {
      const { data, error } = await supabase
        .from('events')
        .insert(eventData as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast({
        title: 'Événement créé',
        description: 'Votre événement a été créé avec succès.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Event> & { id: string }) => {
      const { data: event, error } = await supabase
        .from('events')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast({
        title: 'Événement mis à jour',
        description: 'Les modifications ont été enregistrées.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('events').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast({
        title: 'Événement supprimé',
        description: "L'événement a été supprimé.",
      });
    },
    onError: (error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    events,
    isLoading,
    error,
    createEvent,
    updateEvent,
    deleteEvent,
  };
}
